import * as THREE from "three";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader.js";
import { budgetsFor } from "./quality";
import type { PointLayerDef, QualityTier, RepresentationMode } from "./types";

export interface PointLayerInfo {
  id: string;
  kind: "dense" | "joints";
  visible: boolean;
}

interface LayerEntry {
  def: PointLayerDef;
  object: THREE.Points;
  /** Full xyz float32 before LOD stride (dense only; joints keep as-is). */
  fullPositions: Float32Array;
  userVisible: boolean;
}

const plyLoader = new PLYLoader();
plyLoader.setWithCredentials(true);

/** Dense-cloud subsample stride by quality tier. */
export function denseStrideForTier(tier: QualityTier): number {
  switch (tier) {
    case "low":
      return 8;
    case "medium":
      return 4;
    default:
      return 1;
  }
}

/** Take every `stride`-th point from interleaved xyz float32. */
export function subsamplePositions(positions: Float32Array, stride: number): Float32Array {
  if (stride <= 1 || positions.length < 3) return positions;
  const count = Math.floor(positions.length / 3);
  const outCount = Math.ceil(count / stride);
  const out = new Float32Array(outCount * 3);
  let j = 0;
  for (let i = 0; i < count; i += stride) {
    const i3 = i * 3;
    out[j++] = positions[i3]!;
    out[j++] = positions[i3 + 1]!;
    out[j++] = positions[i3 + 2]!;
  }
  return out;
}

/** Cap point count to maxPoints (further uniform subsample if needed). */
export function capPositions(positions: Float32Array, maxPoints: number): Float32Array {
  if (maxPoints <= 0) return new Float32Array(0);
  const count = Math.floor(positions.length / 3);
  if (count <= maxPoints) return positions;
  const stride = Math.ceil(count / maxPoints);
  return subsamplePositions(positions, stride);
}

/**
 * v1 joints bin: little-endian float32 xyz per point; count = byteLength / 12.
 * Truncates any trailing incomplete triple.
 */
export function parseJointsBin(buffer: ArrayBuffer): Float32Array {
  const byteLength = buffer.byteLength - (buffer.byteLength % 12);
  if (byteLength <= 0) return new Float32Array(0);
  // Copy so we own a clean, aligned Float32Array regardless of source offset.
  return new Float32Array(buffer.slice(0, byteLength));
}

function applyLayerTransform(obj: THREE.Object3D, def: PointLayerDef): void {
  if (def.offset && def.offset.length >= 3) {
    obj.position.set(def.offset[0]!, def.offset[1]!, def.offset[2]!);
  } else {
    obj.position.set(0, 0, 0);
  }
  const s = def.scale != null && Number.isFinite(def.scale) ? def.scale : 1;
  obj.scale.setScalar(s);
}

function makePoints(positions: Float32Array, def: PointLayerDef): THREE.Points {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.computeBoundingSphere();

  const isJoints = def.kind === "joints";
  const material = new THREE.PointsMaterial({
    size: isJoints ? 0.045 : 0.012,
    color: isJoints ? 0xffaa33 : 0x66ccff,
    sizeAttenuation: true,
    transparent: true,
    opacity: isJoints ? 0.95 : 0.85,
    depthWrite: !isJoints,
  });

  const points = new THREE.Points(geometry, material);
  points.name = `pointLayer:${def.id}`;
  points.frustumCulled = true;
  applyLayerTransform(points, def);
  return points;
}

function disposeObject(obj: THREE.Points): void {
  obj.geometry?.dispose();
  const mat = obj.material;
  if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
  else mat?.dispose();
}

function pointCount(positions: Float32Array): number {
  return Math.floor(positions.length / 3);
}

/**
 * Dense PLY + joints float32 bin point layers (H4).
 * LOD: dense clouds are subsampled by quality tier stride; joints stay full.
 * H7: total samples across layers capped by budgets.maxPoints.
 */
export class PointLayers {
  private readonly root: THREE.Group;
  private readonly layers = new Map<string, LayerEntry>();
  private tier: QualityTier = "high";
  private maxPoints = budgetsFor("high").maxPoints;
  private representation: RepresentationMode = "solid";

  constructor(parent: THREE.Object3D) {
    this.root = new THREE.Group();
    this.root.name = "pointLayers";
    parent.add(this.root);
  }

  list(): PointLayerInfo[] {
    return [...this.layers.values()].map((e) => ({
      id: e.def.id,
      kind: e.def.kind,
      visible: e.object.visible,
    }));
  }

  setRepresentation(mode: RepresentationMode): void {
    this.representation = mode;
    // Inspect `points`: turn dense + joints on; per-layer toggles still apply after.
    if (mode === "points") {
      for (const entry of this.layers.values()) {
        entry.userVisible = true;
      }
    }
    this.syncVisibility();
  }

  setTier(tier: QualityTier, maxPoints?: number): void {
    const nextMax = maxPoints ?? budgetsFor(tier).maxPoints;
    if (this.tier === tier && this.maxPoints === nextMax) return;
    this.tier = tier;
    this.maxPoints = nextMax;
    this.reapplyLod();
  }

  setVisible(id: string, visible: boolean): void {
    const entry = this.layers.get(id);
    if (!entry) return;
    entry.userVisible = !!visible;
    this.syncVisibility();
  }

  /** Static v1; reserved for frame-sequence clouds later. */
  update(_timeSeconds: number): void {
    // no-op until multi-frame point sequences ship
  }

  async load(
    defs: PointLayerDef[] | undefined,
    tier: QualityTier,
    maxPoints?: number,
  ): Promise<void> {
    this.clear();
    this.tier = tier;
    this.maxPoints = maxPoints ?? budgetsFor(tier).maxPoints;
    if (!defs?.length) return;

    const stride = denseStrideForTier(tier);
    let remaining = this.maxPoints;

    // Load sequentially so remaining budget is accurate across layers.
    for (const def of defs) {
      if (!def?.id || !def.url) continue;
      if (remaining <= 0) {
        console.info(
          `acroHumanScene: maxPoints=${this.maxPoints} exhausted; skip layer '${def.id}'`,
        );
        continue;
      }
      try {
        if (def.kind === "dense") {
          remaining -= await this.loadDense(def, stride, remaining);
        } else if (def.kind === "joints") {
          remaining -= await this.loadJoints(def, remaining);
        }
      } catch (err) {
        console.warn(`acroHumanScene: point layer '${def.id}' failed`, err);
      }
    }
    this.syncVisibility();
  }

  private reapplyLod(): void {
    const stride = denseStrideForTier(this.tier);
    let remaining = this.maxPoints;
    for (const entry of this.layers.values()) {
      let next: Float32Array;
      if (entry.def.kind === "dense") {
        next = capPositions(subsamplePositions(entry.fullPositions, stride), remaining);
      } else {
        next = capPositions(entry.fullPositions, remaining);
      }
      remaining = Math.max(0, remaining - pointCount(next));
      entry.object.geometry.dispose();
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute("position", new THREE.BufferAttribute(next, 3));
      geometry.computeBoundingSphere();
      entry.object.geometry = geometry;
    }
  }

  private async loadDense(
    def: PointLayerDef,
    stride: number,
    remaining: number,
  ): Promise<number> {
    const geometry = await plyLoader.loadAsync(def.url);
    const attr = geometry.getAttribute("position") as THREE.BufferAttribute | undefined;
    if (!attr || attr.itemSize < 3 || attr.count <= 0) {
      geometry.dispose();
      return 0;
    }

    const full = new Float32Array(attr.count * 3);
    for (let i = 0; i < attr.count; i++) {
      full[i * 3] = attr.getX(i);
      full[i * 3 + 1] = attr.getY(i);
      full[i * 3 + 2] = attr.getZ(i);
    }
    geometry.dispose();

    const positions = capPositions(subsamplePositions(full, stride), remaining);
    const points = makePoints(positions, def);
    this.root.add(points);
    this.layers.set(def.id, {
      def,
      object: points,
      fullPositions: full,
      userVisible: true,
    });
    return pointCount(positions);
  }

  private async loadJoints(def: PointLayerDef, remaining: number): Promise<number> {
    const res = await fetch(def.url, { credentials: "include" });
    if (!res.ok) {
      throw new Error(`joints fetch ${res.status} for ${def.url}`);
    }
    const buffer = await res.arrayBuffer();
    const full = parseJointsBin(buffer);
    if (full.length < 3) return 0;

    const positions = capPositions(full, remaining);
    const points = makePoints(positions, def);
    this.root.add(points);
    this.layers.set(def.id, {
      def,
      object: points,
      fullPositions: full,
      userVisible: true,
    });
    return pointCount(positions);
  }

  private syncVisibility(): void {
    for (const entry of this.layers.values()) {
      entry.object.visible = entry.userVisible;
    }
  }

  clear(): void {
    for (const entry of this.layers.values()) {
      this.root.remove(entry.object);
      disposeObject(entry.object);
    }
    this.layers.clear();
  }

  dispose(): void {
    this.clear();
    this.root.parent?.remove(this.root);
  }
}
